import { EventEmitter, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { Subjects } from '../interface/subject';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class ClassroomService {
  selectedEvents = new EventEmitter<any>();
  selectedSubjectTab = new EventEmitter<Subjects>();
  constructor(private httpService: HttpService) {}

  async getAllClassrooms() {
    try {
      const allClassroomsToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom}`),
      );
      return this.httpService.decodeToken(allClassroomsToken);
    } catch (error) {
      throw error;
    }
  }

  async getClassroomByUserId(queryParams: object) {
    try {
      const classroomsToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom}`, {
          params: queryParams,
        }),
      );

      return this.httpService.decodeToken(classroomsToken);
    } catch (error) {
      throw error;
    }
  }

  async getUnarchiveClassroomListByUserId(queryParams: object) {
    try {
      const classroomsToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_unarchive_classroom_list_by_user_id}`, {
          params: queryParams,
        }),
      );

      return classroomsToken;
      // return this.httpService.decodeToken(classroomsToken);
    } catch (error) {
      throw error;
    }
  }

  async getMockClassroomByUserId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.mock_school_data}`, {
          params: queryParams,
        }),
      );

      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getClassroomBySubjectAndGrade(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom_by_subject_and_grade}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getClassroomByClassroomCodes(body) {
    try {
      const classrooms = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom_by_classroom_codes}`, body),
      );

      return this.httpService.decodeToken(classrooms);
    } catch (error) {
      throw error;
    }
  }

  async addClassroom(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async checkSubjectHours(body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_empty_teacher_subject_hours}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async addMockClassroom(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.mock_school_data}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updateClassroom(id: number, body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async deletClassroom(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async classroomById(id: number) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom}/${id}`),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }

  async trialClassroomById(id: number) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.mock_school_data}/${id}`),
      );
      return token;
    } catch (error) {
      throw error;
    }
  }

  async joinClassroom(body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.StudentByUserId}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async classroomByUserId(body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom_by_user_id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateMultipleClassrooms(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.update_multiple_classrooms}`, body),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getTeacherLessonPlanByUserAndClassroomId(queryParams: Object) {
    try {
      const classroomsToken = await firstValueFrom(
        this.httpService.get<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.teacher_lesson_plan_by_user_and_classroom_id}`,
          {
            params: queryParams,
          },
        ),
      );

      return classroomsToken;
      // return this.httpService.decodeToken(classroomsToken);
    } catch (error) {
      throw error;
    }
  }

  async addAutoClassroomDataToDB(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.auto_populate_classrooms}`, body),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAutoClassroomDataByClassroomId(queryParams) {
    try {
      const classroomsToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_auto_classroom_data_by_classroom_id}`, {
          params: queryParams,
        }),
      );
      return classroomsToken;
      // return this.httpService.decodeToken(classroomsToken);
    } catch (error) {
      throw error;
    }
  }

  async updateAutoClassroomData(id: number, body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.auto_populate_classrooms}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getAllPacingPlanBySubjectAndGradeId(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_pacing_plan_list_by_subject_and_grades_id}`,
          body,
        ),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getClassroomByPages(queryParams: object) {
    try {
      const classroomsToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_classroom_by_pages}`, {
          params: queryParams,
        }),
      );

      return this.httpService.decodeTokenPagination(classroomsToken);
    } catch (error) {
      throw error;
    }
  }

  async getClassroomByClassroomCodesByPages(body) {
    try {
      const classrooms = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.classroom_by_classroom_codes_by_pages}`, body),
      );
      return this.httpService.decodeTokenPagination(classrooms);
    } catch (error) {
      throw error;
    }
  }


}
